'use client'

import { ClipboardIcon, MapPinIcon, UserIcon, WifiIcon } from '@heroicons/react/24/outline'
import FormCheckbox from '@tebuto/forms/Checkbox'
import Mandatory from '@tebuto/forms/Mandatory'
import SegmentedButton from '@tebuto/forms/SegmentedButton'
import { getDateShort, getTime } from '@tebuto/functions/datetime'
import { inPracticeZone } from '@tebuto/functions/practice-time'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { isValidDachZip } from '@tebuto/functions/validate'
import { CurrentClientContext } from '@tebuto/guards/current-client-context'
import { LegalConsentLabel } from '@tebuto/legal/LegalConsentLabel'
import { CaptchaContext, CaptchaProvider, CaptchaWidget } from '@tebuto/providers/CaptchaProvider'
import Heading from '@tebuto/typography/Heading'
import LargeText from '@tebuto/typography/LargeText'
import MediumText from '@tebuto/typography/MediumText'
import SmallText from '@tebuto/typography/SmallText'
import { Form, Formik, useFormikContext } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import { type JSX, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { boolean, mixed, ObjectShape, object, string } from 'yup'
import { BookingWidgetContext } from '../booking-widget/context'
import { WidgetButton } from '../WidgetButton'
import BookingAddressFields from './BookingAddressFields'
import BookingBackButton from './BookingBackButton'
import { BookingContext } from './context'
import {
    isAlreadyBookedErrorType,
    resolveCheckoutCustomerFields,
    resolveEventLocation,
    resolveEventOnlineMeetingProvider,
    useGetMeetingProviderLabel
} from './functions'
import { useParsedPrice } from './hooks'
import { savePendingCheckout } from './payment/pending-checkout-storage'
import {
    CreditOverviewItem,
    EventCategory,
    EventLocation,
    EventLocationSelection,
    EventTherapist,
    MeetingProvider,
    ParsedBookableEvent,
    Therapist
} from './types'

function buildBookingAddressYupShape() {
    return object().shape({
        streetAndNumber: string().required('Pflichtfeld'),
        city: object().shape({
            zip: string()
                .required('Pflichtfeld')
                .test('zip', 'Ungültige Postleitzahl', (value) => isValidDachZip(value)),
            name: string().required('Pflichtfeld')
        })
    })
}

export type BookEventFormValues = {
    outageFeeConsent: boolean
    selectedLocation: EventLocation | null
    termsAndConditionsConfirmed?: boolean
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
    address?: {
        streetAndNumber: string
        city: {
            zip: string
            name: string
        }
    }
    birthdate?: string
    [key: string]: any // Allow additional fields
}

interface BookingEventProps {
    submitButtonText?: string
    handleSubmit?: (event: ParsedBookableEvent, selectedLocation: EventLocationSelection) => void
}

export default function BookingEvent(props: BookingEventProps) {
    const { requireCaptcha } = useContext(BookingContext)

    if (requireCaptcha) {
        return (
            <CaptchaProvider>
                <BookingEventWithCaptcha {...props} />
            </CaptchaProvider>
        )
    }

    return <BookingEventContent {...props} />
}

function BookingEventWithCaptcha(props: BookingEventProps) {
    const { captcha } = useContext(CaptchaContext)
    return <BookingEventContent captcha={captcha} {...props} />
}

function BookingEventContent({
    captcha = null,
    submitButtonText,
    handleSubmit
}: { captcha?: string | null } & BookingEventProps): JSX.Element {
    const {
        setState,
        setNavigationDirection,
        setBookingData,
        setBookingResult,
        useBookEvent,
        useUnclaimBooking,
        bookingEvent,
        categories,
        layout,
        requireCaptcha,
        requireTermsConsent,
        additionalBookingFieldsBefore,
        additionalInitialValues,
        additionalBookingValidationSchemaFields,
        credits,
        useCreditsForBooking,
        setUseCreditsForBooking,
        therapist,
        previewMode,
        isClientAuthenticated,
        checkoutRestoreScopeKey
    } = useContext(BookingContext)
    const { setRequireAddress, publicBookingSiteId } = useContext(BookingWidgetContext)
    const isPreviewMode = previewMode ?? false
    const clientWhoAmI = useContext(CurrentClientContext)
    const { t } = useComponentTranslation()

    const { bookEvent } = useBookEvent()
    const { unclaimBooking, isLoading: isUnclaimBookingLoading } = useUnclaimBooking()

    // Ref to hold the latest captcha value
    const captchaRef = useRef(captcha)

    // Update the ref whenever captcha changes
    useEffect(() => {
        captchaRef.current = captcha
    }, [captcha])

    async function back() {
        if (bookingEvent && !isPreviewMode) {
            await unclaimBooking(bookingEvent)
        }
        setNavigationDirection('back')
        setState('calendar')
    }

    const category = useMemo(
        () => (bookingEvent ? categories[bookingEvent.categoryId] : null),
        [bookingEvent?.categoryId, categories]
    )

    const effectiveLocation = useMemo(() => {
        if (!bookingEvent || !category) {
            return EventLocation.NOT_FIXED
        }
        return resolveEventLocation(bookingEvent, categories)
    }, [bookingEvent, category, categories])

    const creditForCategory: CreditOverviewItem | null = useMemo(() => {
        if (!bookingEvent || !credits || credits.length === 0) return null
        return credits.find((c) => c.categoryId === bookingEvent.categoryId && c.remaining > 0) ?? null
    }, [bookingEvent, credits])

    const isUsingCredits = !!creditForCategory && useCreditsForBooking

    const paymentFeatureEnabled =
        bookingEvent?.featurePaymentEnabled ??
        therapist.features?.featurePaymentEnabled ??
        therapist.settings?.featurePaymentEnabled ??
        false

    const requiresAddressForPayment = useMemo(() => {
        if (!bookingEvent || !category) {
            return false
        }
        if (clientWhoAmI?.address) {
            return false
        }
        if (!paymentFeatureEnabled || !category.paymentEnabled || !category.paymentDuringBooking) {
            return false
        }
        if (isUsingCredits) {
            return false
        }
        return Number.parseFloat(category.price) > 0
    }, [bookingEvent, category, clientWhoAmI, paymentFeatureEnabled, isUsingCredits])

    const showClientPaymentAddressFields = requiresAddressForPayment && (isClientAuthenticated ?? false)

    useEffect(() => {
        if (requiresAddressForPayment && !(isClientAuthenticated ?? false)) {
            setRequireAddress?.(true)
        }
    }, [requiresAddressForPayment, isClientAuthenticated, setRequireAddress])

    const addressValidationHandledByPersonalDataFields = useMemo(
        () => additionalBookingValidationSchemaFields != null && 'address' in additionalBookingValidationSchemaFields,
        [additionalBookingValidationSchemaFields]
    )

    const validationSchema = useMemo(() => {
        const schema: ObjectShape = {
            // When using credits, outage fee consent is implicitly given (credits cover outage fees)
            outageFeeConsent: isUsingCredits
                ? boolean()
                : boolean().required().equals([true], 'Bitte stimmen Sie dem Ausfallhonorar zu.'),
            selectedLocation: mixed().required(),
            ...additionalBookingValidationSchemaFields
        }

        if (requireTermsConsent) {
            schema.termsAndConditionsConfirmed = boolean()
                .required('Bitte stimmen Sie den AGB zu.')
                .equals([true], 'Bitte stimmen Sie den AGB zu.')
        }

        if (requiresAddressForPayment && !addressValidationHandledByPersonalDataFields) {
            schema.address = buildBookingAddressYupShape()
        }

        return object().shape(schema)
    }, [
        additionalBookingValidationSchemaFields,
        requireTermsConsent,
        isUsingCredits,
        requiresAddressForPayment,
        addressValidationHandledByPersonalDataFields
    ])

    if (!bookingEvent || !category) {
        return <div>{t('booking.bookEvent.eventNotFound')}</div>
    }

    const submit = async (values: BookEventFormValues) => {
        if (handleSubmit) {
            return handleSubmit(bookingEvent, values.selectedLocation as unknown as EventLocationSelection)
        }

        try {
            if (!bookingEvent) {
                console.error('No booking event found')
                return
            }

            if (requireCaptcha && !captchaRef.current) {
                console.error('No captcha found')
                return
            }

            if (isPreviewMode) {
                return
            }

            const payload: {
                event: ParsedBookableEvent
                selectedLocation: EventLocationSelection
            } & Record<string, any> = {
                event: bookingEvent,
                selectedLocation: (values.selectedLocation || effectiveLocation) as unknown as EventLocationSelection
            }

            if (additionalBookingValidationSchemaFields) {
                for (const additionalFieldName of Object.keys(additionalBookingValidationSchemaFields)) {
                    if (additionalFieldName in values) {
                        payload[additionalFieldName] = values[additionalFieldName as keyof BookEventFormValues]
                    }
                }
            }

            if (requireCaptcha) {
                payload.captcha = captchaRef.current
            }

            if (isUsingCredits) {
                payload.useCredits = true
            }

            if (values.address) {
                payload.address = values.address
            }

            const requiresPayment =
                paymentFeatureEnabled &&
                category.paymentEnabled &&
                category.paymentDuringBooking &&
                Number.parseFloat(category.price) > 0 &&
                !isUsingCredits

            if (requiresPayment) {
                const customerFields = resolveCheckoutCustomerFields(values, clientWhoAmI)
                const nextBookingData = { ...values, ...customerFields }
                setBookingData(nextBookingData)
                if (checkoutRestoreScopeKey) {
                    savePendingCheckout({
                        scopeKey: checkoutRestoreScopeKey,
                        bookingEvent,
                        bookingData: nextBookingData,
                        publicBookingSiteId
                    })
                }
                setState('payment')
            } else {
                const booking = await bookEvent(payload)

                setBookingResult(booking)
                setState('success')
            }

            setNavigationDirection('forward')
        } catch (error) {
            console.error('Error booking event', error)

            if (error instanceof Error && !isAlreadyBookedErrorType(error.message)) {
                setState('failure')
                setNavigationDirection('back')
            }
        }
    }

    return (
        <Formik<BookEventFormValues>
            initialValues={{
                outageFeeConsent: !category.outageFeeEnabled,
                selectedLocation:
                    effectiveLocation === EventLocation.NOT_FIXED ? EventLocation.ONSITE : effectiveLocation,
                ...(showClientPaymentAddressFields
                    ? { address: { streetAndNumber: '', city: { zip: '', name: '' } } }
                    : {}),
                ...additionalInitialValues
            }}
            validateOnBlur
            validateOnMount
            validationSchema={validationSchema}
            onSubmit={submit}
        >
            {layout === 'large' && additionalBookingFieldsBefore ? (
                <>
                    <SyncOverviewSelectedLocation />
                    <LargeLayout
                        back={back}
                        category={category}
                        effectiveLocation={effectiveLocation}
                        isUnclaimBookingLoading={isUnclaimBookingLoading}
                        submitButtonText={submitButtonText}
                        previewMode={isPreviewMode}
                        creditForCategory={creditForCategory}
                        isUsingCredits={isUsingCredits}
                        onToggleCredits={setUseCreditsForBooking}
                        paymentFeatureEnabled={paymentFeatureEnabled}
                        showClientPaymentAddressFields={showClientPaymentAddressFields}
                        addressValidationHandledByPersonalDataFields={addressValidationHandledByPersonalDataFields}
                    />
                </>
            ) : (
                <>
                    <SyncOverviewSelectedLocation />
                    <SmallLayout
                        back={back}
                        category={category}
                        effectiveLocation={effectiveLocation}
                        isUnclaimBookingLoading={isUnclaimBookingLoading}
                        submitButtonText={submitButtonText}
                        previewMode={isPreviewMode}
                        creditForCategory={creditForCategory}
                        isUsingCredits={isUsingCredits}
                        onToggleCredits={setUseCreditsForBooking}
                        paymentFeatureEnabled={paymentFeatureEnabled}
                        showClientPaymentAddressFields={showClientPaymentAddressFields}
                    />
                </>
            )}
        </Formik>
    )
}

const EMPTY_THERAPIST_ADDRESS: Therapist['address'] = {
    streetAndNumber: '',
    additionalInformation: null,
    city: { name: '', zip: '' }
}

export function resolveEventTherapist(
    eventTherapist: EventTherapist | undefined,
    therapists: Record<number, EventTherapist>,
    fallbackTherapist: Therapist
): Therapist {
    const resolved = eventTherapist ? (therapists[eventTherapist.id] ?? eventTherapist) : null
    if (!resolved?.firstName && !resolved?.lastName) {
        return {
            ...fallbackTherapist,
            address: fallbackTherapist.address ?? EMPTY_THERAPIST_ADDRESS
        }
    }
    const address = resolved.address ?? fallbackTherapist.address ?? EMPTY_THERAPIST_ADDRESS
    return {
        name: resolved.name,
        firstName: resolved.firstName,
        lastName: resolved.lastName,
        address
    }
}

interface LayoutProps {
    back: () => void
    category: EventCategory
    effectiveLocation: EventLocation
    isUnclaimBookingLoading: boolean
    submitButtonText?: string
    previewMode: boolean
    creditForCategory: CreditOverviewItem | null
    isUsingCredits: boolean
    onToggleCredits: (useCredits: boolean) => void
    paymentFeatureEnabled: boolean
    showClientPaymentAddressFields: boolean
    addressValidationHandledByPersonalDataFields?: boolean
}

export function BookingOverview({
    bookingEvent,
    category,
    therapist,
    selectedLocation,
    effectiveLocation,
    effectiveOnlineMeetingProvider,
    isUsingCredits = false
}: {
    bookingEvent: ParsedBookableEvent
    category: EventCategory
    therapist: Therapist
    selectedLocation?: EventLocation | null
    effectiveLocation?: EventLocation
    effectiveOnlineMeetingProvider?: MeetingProvider
    isUsingCredits?: boolean
}): JSX.Element {
    const { t } = useComponentTranslation()
    const { price, priceLabel } = useParsedPrice(category.price)

    const getMeetingProviderLabel = useGetMeetingProviderLabel()
    const resolvedLocation = effectiveLocation ?? resolveEventLocation(bookingEvent, { [category.id]: category })
    const resolvedOnlineMeetingProvider =
        effectiveOnlineMeetingProvider ?? resolveEventOnlineMeetingProvider(bookingEvent, category)

    const isOnsite = resolvedLocation === EventLocation.ONSITE || selectedLocation === EventLocation.ONSITE
    const durationMinutes = bookingEvent.endDatetime.diff(bookingEvent.startDatetime).as('minutes')

    return (
        <div
            className="flex flex-col w-full h-min border widget-border shadow-md rounded-lg widget-bg-field lg:col-span-2"
            data-testid="booking-event-overview"
        >
            <div className="h-1.5 w-full rounded-t-lg widget-bg-primary-500" />
            <div className="flex flex-col gap-4 p-8">
                <div className="relative flex flex-col">
                    <Heading className="font-semibold widget-text-primary" level={2}>
                        {getTime(inPracticeZone(bookingEvent.startDatetime))} Uhr
                    </Heading>
                    <LargeText className="widget-text-secondary">
                        {getDateShort(inPracticeZone(bookingEvent.startDatetime))}
                    </LargeText>
                </div>
                <div className="flex flex-col gap-3 mt-4 text-font-normal">
                    <div className="flex gap-2 items-center">
                        <ClipboardIcon className="widget-text-secondary opacity-60 size-5" />
                        <MediumText className="widget-text-secondary">
                            {category.name}, {durationMinutes} Min.
                        </MediumText>
                    </div>
                    <div className="flex gap-2 items-center">
                        <UserIcon className="widget-text-secondary opacity-60 size-5" />
                        <MediumText className="widget-text-secondary">
                            {therapist.firstName} {therapist.lastName}
                        </MediumText>
                    </div>
                    <div className="flex gap-2 line-clamp-3">
                        <MapPinIcon className="size-5 widget-text-secondary opacity-60 mt-0.5" />
                        <div className="min-h-18">
                            {isOnsite ? (
                                <>
                                    <MediumText className="widget-text-secondary">
                                        {therapist.address.streetAndNumber}
                                    </MediumText>
                                    {therapist.address.additionalInformation && (
                                        <MediumText className="widget-text-secondary">
                                            {therapist.address.additionalInformation}
                                        </MediumText>
                                    )}
                                    <MediumText className="widget-text-secondary">
                                        {therapist.address.city.zip} {therapist.address.city.name}
                                    </MediumText>
                                </>
                            ) : (
                                <MediumText className="widget-text-secondary">
                                    {resolvedOnlineMeetingProvider
                                        ? getMeetingProviderLabel(resolvedOnlineMeetingProvider)
                                        : t('online')}
                                </MediumText>
                            )}
                        </div>
                    </div>
                </div>
                <hr className="my-4 widget-border" />
                <div className="flex justify-between">
                    {isUsingCredits ? (
                        <span className="text-base widget-text-primary font-semibold">{t('free')}</span>
                    ) : price > 0 ? (
                        <>
                            <span className="text-base widget-text-primary font-semibold">
                                {t('booking.bookEvent.amount')}
                            </span>
                            <div className="flex flex-col items-end">
                                <span className="text-base widget-text-primary font-semibold">
                                    {priceLabel}
                                    {t('euro')}
                                </span>
                                {!isUsingCredits && Number.parseFloat(category.taxRate) > 0 && (
                                    <span className="text-[0.5rem] widget-text-secondary">
                                        {t('booking.bookEvent.taxRateIncluded', {
                                            taxRate: category.taxRate
                                        })}
                                    </span>
                                )}
                            </div>
                        </>
                    ) : (
                        <span className="text-base widget-text-primary font-semibold">{t('free')}</span>
                    )}
                </div>
            </div>
        </div>
    )
}

function SyncOverviewSelectedLocation() {
    const { values } = useFormikContext<BookEventFormValues>()
    const { setOverviewSelectedLocation } = useContext(BookingContext)

    useEffect(() => {
        setOverviewSelectedLocation(values.selectedLocation)
    }, [values.selectedLocation, setOverviewSelectedLocation])

    return null
}

function SmallLayout({
    back,
    isUnclaimBookingLoading,
    category,
    effectiveLocation,
    submitButtonText,
    previewMode,
    creditForCategory,
    isUsingCredits,
    onToggleCredits,
    paymentFeatureEnabled,
    showClientPaymentAddressFields
}: LayoutProps): JSX.Element {
    const { t } = useComponentTranslation()
    const { price, priceLabel } = useParsedPrice(category.price)
    const { values, isSubmitting, isValid, submitForm } = useFormikContext<BookEventFormValues>()
    const {
        setBookingData,
        setNavigationDirection,
        setState,
        requireCaptcha,
        requireTermsConsent,
        additionalBookingFieldsBefore,
        bookingNote,
        onClickAlreadyHaveAccount,
        isClientAuthenticated,
        therapist
    } = useContext(BookingContext)
    const clientWhoAmI = useContext(CurrentClientContext)

    const requiresPayment =
        paymentFeatureEnabled &&
        category.paymentEnabled &&
        category.paymentDuringBooking &&
        price > 0 &&
        !isUsingCredits

    const showAlreadyHaveAccountLink = !!onClickAlreadyHaveAccount && !isClientAuthenticated && !previewMode

    const openPaymentPreview = () => {
        const customerFields = resolveCheckoutCustomerFields(values, clientWhoAmI)
        setBookingData({ ...values, ...customerFields })
        setNavigationDirection('forward')
        setState('payment')
    }

    const handleAlreadyHaveAccountClick = () => {
        onClickAlreadyHaveAccount?.()
        setNavigationDirection('forward')
        setState('login')
    }

    return (
        <div className="flex flex-col w-full">
            <BookingBackButton onClick={back} label={t('back')} isLoading={isUnclaimBookingLoading} />
            <Form className="flex flex-col grow gap-10 relative mb-0">
                {showClientPaymentAddressFields && <BookingAddressFields />}
                {additionalBookingFieldsBefore}
                {effectiveLocation === EventLocation.NOT_FIXED && (
                    <div className="grid gap-4">
                        <div className="text-lg font-medium widget-text-primary">{t('booking.bookEvent.where')}</div>
                        <SegmentedButton
                            name="selectedLocation"
                            containerClassName="widget-bg-primary-50 widget-border-primary-200"
                            buttonLabelClassNameInactive="widget-text-primary-color-400"
                            buttonIconClassNameInactive="widget-text-primary-color-400"
                            buttonLabelClassNameActive="widget-text-primary-color-700"
                            buttonIconClassNameActive="widget-text-primary-color-700"
                            motionClassName="widget-bg-field shadow-sm widget-border-primary-300"
                            options={[
                                {
                                    label: t('onsite'),
                                    value: EventLocation.ONSITE,
                                    icon: MapPinIcon,
                                    testId: 'booking-location-onsite'
                                },
                                {
                                    label: t('online'),
                                    value: EventLocation.VIRTUAL,
                                    icon: WifiIcon,
                                    testId: 'booking-location-online'
                                }
                            ]}
                        />
                    </div>
                )}
                {creditForCategory && (
                    <CreditNotice
                        credit={creditForCategory}
                        isUsingCredits={isUsingCredits}
                        onToggle={onToggleCredits}
                    />
                )}
                <div>
                    <div className="text-lg font-medium mb-4 widget-text-primary">{t('booking.bookEvent.notice')}</div>
                    <MediumText className="text-justify widget-text-secondary">
                        {isUsingCredits
                            ? t('booking.bookEvent.free')
                            : price > 0
                              ? t('booking.bookEvent.price', { price: priceLabel })
                              : t('booking.bookEvent.free')}
                    </MediumText>
                    {bookingNote && (
                        <MediumText className="text-justify widget-text-secondary mt-2">{bookingNote}</MediumText>
                    )}
                    {previewMode && (
                        <div className="mt-4 rounded-lg border widget-border-primary-200 widget-bg-primary-50 p-3">
                            <SmallText className="widget-text-secondary">{t('booking.preview.notice')}</SmallText>
                        </div>
                    )}
                    {category.outageFeeEnabled && !isUsingCredits && (
                        <div className="flex gap-4 my-6 border rounded-lg p-3 widget-bg-field">
                            <FormCheckbox
                                name="outageFeeConsent"
                                mandatory={true}
                                showErrors={false}
                                className={'flex items-center widget-text-secondary'}
                            />
                            <label className="widget-text-secondary" htmlFor="outageFeeConsent">
                                <p className="widget-text-secondary text-[0.75rem] relative pr-4">
                                    {t('booking.bookEvent.outageConsequences', {
                                        hours: category.outageFeeHours,
                                        fee: category.outageFeePrice
                                    })}{' '}
                                    <Mandatory mandatory />
                                </p>
                            </label>
                        </div>
                    )}
                    {requireTermsConsent && (
                        <div className="flex gap-4 my-6 border rounded-lg p-3 widget-bg-field">
                            <FormCheckbox
                                name={'termsAndConditionsConfirmed'}
                                showErrors={false}
                                className={'flex items-center widget-text-secondary'}
                            />
                            <label className="widget-text-secondary" htmlFor={'termsAndConditionsConfirmed'}>
                                <p className="widget-text-secondary text-[0.75rem] relative pr-4">
                                    <LegalConsentLabel
                                        variant="booking"
                                        therapistName={therapist.name}
                                        termsUrl={therapist.settings?.termsAndConditionsUrl}
                                        privacyUrl={therapist.settings?.dataPrivacyAgreementUrl}
                                        linkClassName="widget-text-primary-color-700 underline"
                                    />
                                    <Mandatory mandatory />
                                </p>
                            </label>
                        </div>
                    )}
                </div>
                <div className="mt-4 flex w-full flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                    {showAlreadyHaveAccountLink && (
                        <button
                            type="button"
                            onClick={handleAlreadyHaveAccountClick}
                            className="min-w-0 text-left text-xs underline widget-text-secondary hover:widget-text-primary sm:flex-[1_1_38%]"
                            data-testid="widget-already-have-account"
                        >
                            Ich habe bereits ein Konto — anmelden
                        </button>
                    )}
                    <div
                        className={
                            showAlreadyHaveAccountLink
                                ? 'flex w-full min-w-0 items-end gap-3 sm:flex-[1_1_62%]'
                                : 'flex w-full min-w-0 items-end justify-end gap-3'
                        }
                    >
                        {previewMode && requiresPayment && (
                            <WidgetButton
                                className="min-w-0 flex-1"
                                layout="outline"
                                type="button"
                                onClick={openPaymentPreview}
                                data-testid="booking-preview-open-payment"
                            >
                                {t('booking.preview.openPaymentPreview')}
                            </WidgetButton>
                        )}
                        <div className="flex min-w-0 flex-1 flex-col items-stretch">
                            <WidgetButton
                                className="w-full min-w-0"
                                disabled={previewMode || isSubmitting || !isValid}
                                isLoading={isSubmitting}
                                type="button"
                                data-testid={requiresPayment ? 'booking-submit-payment' : 'booking-submit'}
                                onClick={async (e) => {
                                    e.preventDefault()
                                    if (previewMode) {
                                        return
                                    }
                                    if (isValid && !isSubmitting) {
                                        await submitForm()
                                    }
                                }}
                            >
                                {submitButtonText ??
                                    (requiresPayment
                                        ? t('booking.bookEvent.goToPayment')
                                        : t('booking.bookEvent.book'))}
                            </WidgetButton>
                            <div id="altcha-anchor" className="justify-end" />
                            {requireCaptcha && <CaptchaWidget floatinganchor="div#altcha-anchor" />}
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    )
}

function LargeLayout({
    back,
    isUnclaimBookingLoading,
    category,
    effectiveLocation,
    submitButtonText,
    previewMode,
    creditForCategory,
    isUsingCredits,
    onToggleCredits,
    paymentFeatureEnabled,
    showClientPaymentAddressFields,
    addressValidationHandledByPersonalDataFields = false
}: LayoutProps): JSX.Element {
    const { t } = useComponentTranslation()
    const { price, priceLabel } = useParsedPrice(category.price)
    const { values, isSubmitting, isValid, submitForm } = useFormikContext<BookEventFormValues>()
    const {
        setBookingData,
        setNavigationDirection,
        setState,
        requireCaptcha,
        requireTermsConsent,
        additionalBookingFieldsBefore,
        additionalBookingValidationSchemaFields,
        bookingNote,
        onClickAlreadyHaveAccount,
        isClientAuthenticated,
        therapist
    } = useContext(BookingContext)
    const clientWhoAmI = useContext(CurrentClientContext)

    const showAlreadyHaveAccountLink = !!onClickAlreadyHaveAccount && !isClientAuthenticated && !previewMode

    const handleAlreadyHaveAccountClick = () => {
        onClickAlreadyHaveAccount?.()
        setNavigationDirection('forward')
        setState('login')
    }

    const [formViewState, setFormViewState] = useState<'personalData' | 'eventAndConfirmations'>('personalData')
    const [formNavigationDirection, setFormNavigationDirection] = useState<'forward' | 'back' | null>(null)
    const requiresPayment =
        paymentFeatureEnabled &&
        category.paymentEnabled &&
        category.paymentDuringBooking &&
        price > 0 &&
        !isUsingCredits

    const nextButtonEnabled = useMemo(() => {
        const schemaFields: ObjectShape = { ...additionalBookingValidationSchemaFields }
        if (showClientPaymentAddressFields && !addressValidationHandledByPersonalDataFields) {
            schemaFields.address = buildBookingAddressYupShape()
        }
        if (Object.keys(schemaFields).length > 0) {
            return object().shape(schemaFields).isValidSync(values)
        }
        return true
    }, [
        values,
        additionalBookingValidationSchemaFields,
        showClientPaymentAddressFields,
        addressValidationHandledByPersonalDataFields
    ])

    const formStepVariants = useMemo(
        () => ({
            initial: (direction: string) => ({
                x: direction === 'back' ? '-100%' : '100%',
                opacity: 0
            }),
            animate: {
                x: 0,
                opacity: 1,
                transition: { duration: 0.3 }
            },
            exit: (direction: string) => ({
                x: direction === 'back' ? '100%' : '-100%',
                opacity: 0,
                transition: { duration: 0.3 }
            })
        }),
        []
    )

    const handleNextStep = () => {
        setFormNavigationDirection('forward')
        setFormViewState('eventAndConfirmations')
    }

    const handlePreviousStep = () => {
        setFormNavigationDirection('back')
        setFormViewState('personalData')
    }

    const openPaymentPreview = () => {
        const customerFields = resolveCheckoutCustomerFields(values, clientWhoAmI)
        setBookingData({ ...values, ...customerFields })
        setNavigationDirection('forward')
        setState('payment')
    }

    return (
        <div className="flex flex-col w-full">
            <BookingBackButton onClick={back} label={t('back')} isLoading={isUnclaimBookingLoading} />
            <Form className="flex flex-col grow gap-10 relative mb-0">
                <div className="relative overflow-hidden p-2">
                    <AnimatePresence custom={formNavigationDirection} mode="wait">
                        {formViewState === 'personalData' && (
                            <motion.div
                                key="personalData"
                                className="grid gap-10 w-full -m-2 p-2"
                                custom={formNavigationDirection}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                variants={formStepVariants}
                            >
                                {showClientPaymentAddressFields && <BookingAddressFields />}
                                {additionalBookingFieldsBefore}
                                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                                    {showAlreadyHaveAccountLink && (
                                        <button
                                            type="button"
                                            onClick={handleAlreadyHaveAccountClick}
                                            className="min-w-0 text-left text-xs underline widget-text-secondary hover:widget-text-primary sm:flex-[1_1_38%]"
                                            data-testid="widget-already-have-account"
                                        >
                                            Ich habe bereits ein Konto — anmelden
                                        </button>
                                    )}
                                    <WidgetButton
                                        className={
                                            showAlreadyHaveAccountLink
                                                ? 'w-full min-w-0 sm:flex-[1_1_62%]'
                                                : 'w-full min-w-0 sm:ml-auto sm:max-w-xs'
                                        }
                                        onClick={handleNextStep}
                                        disabled={!nextButtonEnabled}
                                        type="button"
                                    >
                                        {t('next')}
                                    </WidgetButton>
                                </div>
                            </motion.div>
                        )}

                        {formViewState === 'eventAndConfirmations' && (
                            <motion.div
                                key="eventAndConfirmations"
                                className="flex flex-col justify-between gap-10 w-full h-full -m-2 p-2"
                                custom={formNavigationDirection}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                variants={formStepVariants}
                            >
                                <div className="flex flex-col gap-10">
                                    {effectiveLocation === EventLocation.NOT_FIXED && (
                                        <div className="grid gap-4">
                                            <div className="text-lg font-medium widget-text-primary">
                                                {t('booking.bookEvent.where')}
                                            </div>
                                            <SegmentedButton
                                                name="selectedLocation"
                                                containerClassName="widget-bg-primary-50 widget-border-primary-200"
                                                buttonLabelClassNameInactive="widget-text-primary-color-400"
                                                buttonIconClassNameInactive="widget-text-primary-color-400"
                                                buttonLabelClassNameActive="widget-text-primary-color-700"
                                                buttonIconClassNameActive="widget-text-primary-color-700"
                                                motionClassName="widget-bg-field shadow-sm widget-border-primary-300"
                                                options={[
                                                    {
                                                        label: t('onsite'),
                                                        value: EventLocation.ONSITE,
                                                        icon: MapPinIcon,
                                                        testId: 'booking-location-onsite'
                                                    },
                                                    {
                                                        label: t('online'),
                                                        value: EventLocation.VIRTUAL,
                                                        icon: WifiIcon,
                                                        testId: 'booking-location-online'
                                                    }
                                                ]}
                                            />
                                        </div>
                                    )}
                                    {creditForCategory && (
                                        <CreditNotice
                                            credit={creditForCategory}
                                            isUsingCredits={isUsingCredits}
                                            onToggle={onToggleCredits}
                                        />
                                    )}
                                    <div className="pt-6">
                                        <div className="text-lg font-medium mb-4 widget-text-primary">
                                            {t('booking.bookEvent.notice')}
                                        </div>
                                        <MediumText className="text-justify widget-text-secondary">
                                            {isUsingCredits
                                                ? t('booking.bookEvent.free')
                                                : price > 0
                                                  ? t('booking.bookEvent.price', {
                                                        price: priceLabel
                                                    })
                                                  : t('booking.bookEvent.free')}
                                        </MediumText>
                                        {bookingNote && (
                                            <MediumText className="text-justify widget-text-secondary mt-2">
                                                {bookingNote}
                                            </MediumText>
                                        )}
                                        {category.outageFeeEnabled && !isUsingCredits && (
                                            <div className="flex gap-4 my-6 border rounded-lg p-3 widget-bg-field">
                                                <FormCheckbox
                                                    name="outageFeeConsent"
                                                    mandatory={true}
                                                    showErrors={false}
                                                    className={'flex items-center widget-text-secondary'}
                                                />
                                                <label className="widget-text-secondary" htmlFor="outageFeeConsent">
                                                    <p className="widget-text-secondary text-[0.75rem] relative pr-4">
                                                        {t('booking.bookEvent.outageConsequences', {
                                                            hours: category.outageFeeHours,
                                                            fee: category.outageFeePrice
                                                        })}{' '}
                                                        <Mandatory mandatory />
                                                    </p>
                                                </label>
                                            </div>
                                        )}
                                        {requireTermsConsent && (
                                            <div className="flex gap-4 my-6 border rounded-lg p-3 widget-bg-field">
                                                <FormCheckbox
                                                    name={'termsAndConditionsConfirmed'}
                                                    mandatory={true}
                                                    showErrors={false}
                                                    className={'flex items-center widget-text-secondary'}
                                                />
                                                <label
                                                    className="widget-text-secondary"
                                                    htmlFor={'termsAndConditionsConfirmed'}
                                                >
                                                    <p className="widget-text-secondary text-[0.75rem] relative pr-4">
                                                        <LegalConsentLabel
                                                            variant="booking"
                                                            therapistName={therapist.name}
                                                            termsUrl={therapist.settings?.termsAndConditionsUrl}
                                                            privacyUrl={therapist.settings?.dataPrivacyAgreementUrl}
                                                            linkClassName="widget-text-primary-color-700 underline"
                                                        />
                                                        <Mandatory mandatory />
                                                    </p>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex w-full min-w-0 items-end gap-3">
                                    <WidgetButton
                                        className="min-w-0 flex-1"
                                        onClick={handlePreviousStep}
                                        layout="outline"
                                        type="button"
                                        data-testid="booking-form-back-button"
                                    >
                                        {t('back')}
                                    </WidgetButton>
                                    {previewMode && requiresPayment && (
                                        <WidgetButton
                                            className="min-w-0 flex-1"
                                            layout="outline"
                                            type="button"
                                            onClick={openPaymentPreview}
                                            data-testid="booking-preview-open-payment"
                                        >
                                            {t('booking.preview.openPaymentPreview')}
                                        </WidgetButton>
                                    )}
                                    <div className="flex min-w-0 flex-1 flex-col items-stretch">
                                        <WidgetButton
                                            className="w-full min-w-0"
                                            disabled={previewMode || isSubmitting || !isValid}
                                            isLoading={isSubmitting}
                                            type="button"
                                            data-testid={requiresPayment ? 'booking-submit-payment' : 'booking-submit'}
                                            onClick={async (e) => {
                                                e.preventDefault()
                                                if (previewMode) {
                                                    return
                                                }
                                                if (isValid && !isSubmitting) {
                                                    await submitForm()
                                                }
                                            }}
                                        >
                                            {submitButtonText ??
                                                (requiresPayment
                                                    ? t('booking.bookEvent.goToPayment')
                                                    : t('booking.bookEvent.book'))}
                                        </WidgetButton>
                                        <div id="altcha-anchor" className="justify-end" />
                                        {requireCaptcha && <CaptchaWidget floatinganchor="div#altcha-anchor" />}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </Form>
        </div>
    )
}

/** Displays available credit info with opt-in/out toggle */
function CreditNotice({
    credit,
    isUsingCredits,
    onToggle
}: {
    credit: CreditOverviewItem
    isUsingCredits: boolean
    onToggle: (value: boolean) => void
}) {
    return (
        <div className="rounded-lg border widget-border widget-bg-primary-50/50 p-4">
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <p className="text-sm font-medium widget-text-primary">
                        {isUsingCredits
                            ? `Die nächsten ${credit.remaining} Buchungen der Kategorie "${credit.categoryName}" sind für Sie kostenfrei.`
                            : `Sie haben ${credit.remaining} kostenfreie Buchungen für "${credit.categoryName}" verfügbar.`}
                    </p>
                    {!isUsingCredits && (
                        <p className="text-xs widget-text-secondary mt-1">
                            Aktivieren Sie diese Option, um Ihr Guthaben zu verwenden.
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => onToggle(!isUsingCredits)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        isUsingCredits ? 'widget-bg-primary-500' : 'bg-gray-200'
                    }`}
                    role="switch"
                    aria-checked={isUsingCredits}
                    data-testid="credit-toggle"
                >
                    <span
                        className={`pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isUsingCredits ? 'translate-x-5' : 'translate-x-0'
                        }`}
                    />
                </button>
            </div>
        </div>
    )
}
